// IM-XPR-001 (IM-UI-094, IM-UX-109..111) — Tela Hoje: saudação, organização, UM objetivo em destaque com a
// próxima decisão e a consequência, no máximo três itens que pedem atenção (nunca contadores zerados), os cinco
// objetivos e as quatro respostas do que já sabemos (síntese + confiança; evidências recolhidas). Tudo vem das APIs reais.
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MONTH_NAME, type JourneyType, type ObjectiveKey, type PlanningOverviewDto } from '@intelimove/contracts';
import { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { AsyncView } from '../components/States';
import { useToast } from '../components/Toast';
import { signalsAdapter, relativeTime } from '../data/adapters';
import { decisionsApi } from '../data/executive';
import { intelApi } from '../data/intel';
import { journeysApi } from '../data/journeys';
import { sourceRecoApi } from '../data/sources';
import { useResource } from '../hooks/useResource';
import { useCanManage } from '../pages/Targets';
import { useExperience } from './ExperienceContext';
import {
  loadCompetitorFacts, loadJourneyObjectives, loadPageRecommendations, loadPlanningOverview, loadTargetFacts, objectiveHref, phaseConsequence, phaseHeadline, plural,
  stepPlain, summarizeObjectives, type CompetitorFacts, type ObjectiveSummary,
} from './data';
import { ObjectiveCards } from './ObjectiveCards';
import { DecisionAssistant } from '../components/DecisionAssistant';
import { OpportunityRadarPanel } from '../components/OpportunityRadar';
import { topPages } from './pages';

/** Frase de situação sem zeros ("0 confirmados" vira só a parte que existe). */
export function competitorsSentence(confirmed: number, hypotheses: number): string | null {
  const verb = confirmed === 1 ? 'Há' : 'São';
  if (confirmed && hypotheses) return `${verb} ${plural(confirmed, 'concorrente confirmado', 'concorrentes confirmados')} e ${plural(hypotheses, 'hipótese', 'hipóteses')}.`;
  if (confirmed) return `${verb} ${plural(confirmed, 'concorrente confirmado', 'concorrentes confirmados')}.`;
  if (hypotheses) return `${hypotheses === 1 ? 'Há uma hipótese' : `Há ${hypotheses} hipóteses`} de concorrente ainda sem confirmação.`;
  return null;
}

export function greeting(d = new Date()) {
  const h = d.getHours();
  return h < 12 ? 'Bom dia' : h < 18 ? 'Boa tarde' : 'Boa noite';
}

export interface AttentionItem { key: string; title: string; why: string; to: string; tone: 'amber' | 'mint' }
/** No máximo três, na ordem de prioridade; item com zero nunca aparece (IM-UX-111). */
/** IM-JRN-015/016 (IM-UX-162): movimentos do plano — só quando há uma ação possível agora. */
export function planningAttention(p: PlanningOverviewDto | null | undefined): AttentionItem[] {
  if (!p) return [];
  const out: AttentionItem[] = [];
  const t = p.tracking;
  if (t?.dueMonth) out.push({ key: 'realizado', title: `Registrar o realizado de ${MONTH_NAME[t.dueMonth - 1]}`, why: 'Sem ele, o acompanhamento do plano fica parado.', to: '/objetivos/acompanhar-plano?fase=realizado', tone: 'amber' });
  if (t?.reviewDue && t.lastMonth && (t.status === 'ATENCAO' || t.status === 'FORA_DO_RITMO')) out.push({ key: 'desvio', title: `Analisar o desvio de ${MONTH_NAME[t.lastMonth - 1]}`, why: 'Entenda o desvio e decida a correção de rota.', to: '/objetivos/acompanhar-plano?fase=desvios', tone: 'amber' });
  else if (t?.reviewDue && t.lastMonth) out.push({ key: 'revisao', title: `Revisar ${MONTH_NAME[t.lastMonth - 1]} do plano`, why: 'Registre a revisão do mês.', to: '/objetivos/acompanhar-plano?fase=corrigir', tone: 'mint' });
  if (p.plan?.status === 'EM_REVISAO') out.push({ key: 'aprovar', title: `Aprovar as metas de ${p.plan.year}`, why: 'O plano está em revisão, aguardando aprovação.', to: `/objetivos/planejar?ano=${p.plan.year}&fase=plano`, tone: 'amber' });
  else if (p.plan?.status === 'RASCUNHO') out.push({ key: 'cenario', title: `Revisar o cenário recomendado para ${p.plan.year}`, why: 'Escolha o cenário e feche as metas do plano.', to: `/objetivos/planejar?ano=${p.plan.year}&fase=cenarios`, tone: 'mint' });
  else if (!p.plan && p.base.level === 'SEM_DADOS' && p.today.month >= 9) out.push({ key: 'base', title: 'Completar a base financeira', why: `Faturamento e despesas para planejar ${p.planYear}.`, to: '/objetivos/planejar/dados', tone: 'mint' });
  return out;
}

export function attentionItems(x: {
  toClassify?: number; decisions?: number; targetsReview?: number; pages?: number; sourceProblems?: number; highSignals?: number; peopleReview?: number; planning?: AttentionItem[];
}): AttentionItem[] {
  const all: Array<AttentionItem | null> = [
    ...(x.planning ?? []).slice(0, 2),
    x.toClassify ? { key: 'classificar', title: plural(x.toClassify, 'concorrente para classificar', 'concorrentes para classificar'), why: 'Destrava a comparação competitiva.', to: objectiveHref('concorrentes', 'confirmar'), tone: 'amber' } : null,
    x.decisions ? { key: 'decisoes', title: plural(x.decisions, 'decisão pede sua atenção', 'decisões pedem sua atenção'), why: 'Prioridades, conflitos ou revisões na Central.', to: '/decisoes?visao=executiva', tone: 'amber' } : null,
    x.targetsReview ? { key: 'avaliar', title: plural(x.targetsReview, 'empresa para avaliar', 'empresas para avaliar'), why: 'Aprovar ou descartar libera a busca de pessoas.', to: '/objetivos/empresas/carteira?estagio=AGUARDANDO_DECISAO', tone: 'amber' } : null,
    x.pages ? { key: 'paginas', title: plural(x.pages, 'página recomendada para acompanhar', 'páginas recomendadas para acompanhar'), why: 'Prontas para ativar o monitoramento.', to: objectiveHref('concorrentes', 'acompanhar'), tone: 'mint' } : null,
    x.sourceProblems ? { key: 'fontes', title: plural(x.sourceProblems, 'fonte com problema', 'fontes com problema'), why: 'A coleta falhou ou precisa de uma ação sua.', to: '/fontes?visao=problemas', tone: 'amber' } : null,
    x.highSignals ? { key: 'sinais', title: plural(x.highSignals, 'mudança importante no mercado', 'mudanças importantes no mercado'), why: 'Sinais de prioridade alta para avaliar.', to: '/acompanhar', tone: 'amber' } : null,
    x.peopleReview ? { key: 'pessoas', title: plural(x.peopleReview, 'pessoa para conferir', 'pessoas para conferir'), why: 'Decisores encontrados aguardando sua conferência.', to: '/pessoas', tone: 'mint' } : null,
  ];
  return all.filter((i): i is AttentionItem => !!i).slice(0, 3);
}

/** O objetivo em destaque: o em andamento mais recente; senão o primeiro pronto para começar. */
export function pickActive(items: readonly ObjectiveSummary[]): ObjectiveSummary | null {
  const live = items.filter((o) => o.journey && o.journey.journey.lifecycle === 'EM_ANDAMENTO').sort((a, b) => b.journey!.journey.updatedAt.localeCompare(a.journey!.journey.updatedAt));
  return live[0] ?? items.find((o) => o.journey && o.journey.journey.lifecycle === 'PAUSADA') ?? items.find((o) => o.status === 'COMECAR' && o.catalog) ?? null;
}

function StartObjective({ type, label = 'Começar' }: { type: JourneyType; label?: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  return (
    <button type="button" className="xp-primary" disabled={busy} aria-busy={busy} onClick={async () => {
      setBusy(true);
      try { await journeysApi.create({ type }); navigate(objectiveHref(type === 'CONCORRENTES' ? 'concorrentes' : 'ofertas')); }
      catch (e) { toast(e instanceof ApiError ? e.message : 'Não foi possível começar agora.', 'error'); setBusy(false); }
    }}>{busy ? 'Começando…' : label}</button>
  );
}

function Hero({ active, competitors, pages }: { active: ObjectiveSummary | null; competitors: CompetitorFacts | null; pages: number }) {
  const canManage = useCanManage();
  if (!active) {
    return (
      <article className="xp-hero" aria-labelledby="xp-hero-t">
        <p className="xp-eyebrow">Comece por aqui</p>
        <h2 id="xp-hero-t">Escolha o que você quer resolver.</h2>
        <p>O InteliMove organiza o caminho e usa as análises necessárias nos bastidores.</p>
        <div className="xp-hero-actions"><Link className="xp-primary" to="/objetivos">Ver objetivos</Link></div>
      </article>
    );
  }
  const key = active.def.key as ObjectiveKey;
  const d = active.journey;
  if (!d) {
    return (
      <article className="xp-hero" aria-labelledby="xp-hero-t">
        <p className="xp-eyebrow">Comece por aqui</p>
        <h2 id="xp-hero-t">{active.def.title}</h2>
        <p>{active.def.outcome} O InteliMove já tem o mínimo para começar.</p>
        <div className="xp-hero-actions">
          {canManage ? <StartObjective type={active.def.journeyType} /> : <Link className="xp-primary" to={objectiveHref(key)}>Ver o objetivo</Link>}
          <Link className="xp-secondary" to="/objetivos">Ver meus objetivos</Link>
        </div>
        {!canManage && <p className="xp-hero-note">Um administrador da organização pode começar este objetivo.</p>}
      </article>
    );
  }
  const phases = active.phases ?? [];
  const current = phases.find((p) => p.current);
  const support = key === 'concorrentes' && competitors ? competitorsSentence(competitors.confirmed, competitors.hypotheses) : null;
  const consequence = phaseConsequence(phases, current);
  const paused = d.journey.lifecycle === 'PAUSADA';
  return (
    <article className="xp-hero" aria-labelledby="xp-hero-t">
      <p className="xp-eyebrow">{paused ? 'Objetivo pausado' : 'Retome de onde parou'} · {active.def.title}</p>
      <h2 id="xp-hero-t">{phaseHeadline(key, current, { competitors, pages })}</h2>
      <p>{[support, consequence].filter(Boolean).join(' ')}</p>
      {d.nextAction?.stepId && <p className="xp-hero-next"><b>Próximo passo:</b> {stepPlain(d.nextAction.stepId, d.nextAction.title)}</p>}
      <div className="xp-hero-actions">
        <Link className="xp-primary" to={objectiveHref(key, current?.key)}>Continuar</Link>
        <Link className="xp-secondary" to="/objetivos">Ver meus objetivos</Link>
      </div>
    </article>
  );
}

function Attention({ items }: { items: AttentionItem[] }) {
  return (
    <aside className="xp-attention" aria-labelledby="xp-att-t">
      <h2 id="xp-att-t">O que pede atenção</h2>
      {items.length === 0 ? (
        <p className="xp-quiet">Nada pede sua atenção agora. Bom momento para avançar num objetivo ou <Link to="/acompanhar">ver o que mudou</Link>.</p>
      ) : (
        <ul role="list">
          {items.map((i) => (
            <li key={i.key} className={`tone-${i.tone}`}>
              <span className="xp-dot" aria-hidden="true" />
              <div><b>{i.title}</b><span>{i.why}</span></div>
              <Link to={i.to} aria-label={`Abrir: ${i.title}`}>Abrir</Link>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

const ANSWER_LABEL: Record<string, [string, string]> = {
  paraQuemVender: ['Para quem vender?', '/empresas-alvo'], comQuemConversar: ['Com quem falar?', '/pessoas'],
  porQueComprariam: ['Por que comprariam?', '/mercado'], quandoAbordar: ['Quando abordar?', '/sinais'],
};
const CONF_LABEL: Record<string, string> = { ALTA: 'Confiança alta', MEDIA: 'Confiança média', BAIXA: 'Confiança baixa' };
type Answer = { key: string; question: string; answer: string | null; items: Array<{ kind: string; name: string; detail: string }>; evidenceCount: number; confidence: string | null; pending: string[]; updatedAt: string | null };

/** "O que já sabemos": síntese curta + confiança; evidências e pendências em detalhes (IM-UX-111). */
export function KnownAnswers() {
  const { state, reload } = useResource((s) => intelApi.answers(s), []);
  return (
    <AsyncView state={state} onRetry={reload} empty={null} loading="metrics">
      {(d) => (
        <ul className="xp-answers" role="list">
          {(d.answers as Answer[]).map((a) => {
            const [q, to] = ANSWER_LABEL[a.key] ?? [a.question, '/analises'];
            const first = a.items[0];
            const synthesis = a.answer ?? (first ? `${first.name}${first.detail ? ` — ${first.detail}` : ''}` : null);
            return (
              <li key={a.key} className="xp-answer">
                <h3>{q}</h3>
                <p className={synthesis ? 'xp-answer-text' : 'xp-quiet'}>{synthesis ? (synthesis.length > 160 ? `${synthesis.slice(0, 157)}…` : synthesis) : (a.pending[0] ?? 'Ainda não há dados para responder.').replace(/^Ainda não há dados para responder: /, 'Ainda sem resposta. Próximo passo: ')}</p>
                <span className={`xp-conf${a.confidence ? '' : ' none'}`}>{a.confidence ? CONF_LABEL[a.confidence] ?? 'Confiança indefinida' : 'Sem dados ainda'}</span>
                {(a.items.length > 0 || a.evidenceCount > 0) && (
                  <details className="xp-why">
                    <summary>Ver detalhes</summary>
                    {a.items.length > 0 && <ul className="plain-list">{a.items.slice(0, 5).map((i, n) => <li key={n}><b>{i.name}</b> <span className="cell-sub inline">{i.detail}</span></li>)}</ul>}
                    <p className="cell-sub">{plural(a.evidenceCount, 'evidência', 'evidências')}{a.updatedAt ? ` · atualizado ${relativeTime(a.updatedAt)}` : ''}{a.pending.length && synthesis ? ` · ${a.pending.join(' · ')}` : ''}</p>
                    <Link to={to} className="link-btn">Abrir a análise</Link>
                  </details>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </AsyncView>
  );
}

export function Today() {
  const { me } = useAuth();
  const xp = useExperience();
  const { state, reload } = useResource(async (s) => {
    const [j, targets, competitors, overview, decisions, signals, pages, planning] = await Promise.all([
      loadJourneyObjectives(xp.journeys, s).catch(() => ({})),
      loadTargetFacts(s),
      loadCompetitorFacts(s).catch(() => null),
      sourceRecoApi.overview(s).catch(() => null),
      decisionsApi.summary(s).catch(() => null),
      signalsAdapter.summary(s).catch(() => null),
      loadPageRecommendations(s).catch(() => null),
      loadPlanningOverview(s),
    ]);
    return { objectives: summarizeObjectives(j, targets, planning), targets, competitors, overview, decisions, signals, pages, planning };
  }, [xp.journeys]);
  const first = me?.user.name.split(' ')[0] ?? '';
  const org = me?.activeOrganization?.name ?? '';
  useEffect(() => { document.title = 'Hoje · InteliMove'; }, []);
  return (
    <section aria-labelledby="page-title" className="xp-today">
      <header className="xp-head">
        <p className="xp-eyebrow">Seu próximo movimento · {org}</p>
        <h1 id="page-title">{greeting()}, {first}.</h1>
        <p className="xp-lead">O InteliMove organizou o que merece sua atenção. Veja decisões e próximos passos.</p>
      </header>
      <AsyncView state={state} onRetry={reload} empty={null} loading="metrics">
        {(d) => {
          const pagesTop = d.pages ? topPages(d.pages.all).top.length : 0;
          const att = attentionItems({
            toClassify: d.competitors?.toClassify.length, decisions: d.decisions?.attention, targetsReview: d.targets?.targetsReview, pages: pagesTop,
            sourceProblems: d.overview?.problems, highSignals: d.signals?.highPriority, peopleReview: d.targets?.peopleReview, planning: planningAttention(d.planning),
          });
          return (
            <>
              <div className="xp-hero-row">
                <Hero active={pickActive(d.objectives)} competitors={d.competitors} pages={pagesTop} />
                <Attention items={att} />
              </div>
              <div className="xp-section-head">
                <div><h2>O que você quer resolver?</h2><p>Escolha um objetivo. O InteliMove organiza o caminho e usa as análises necessárias nos bastidores.</p></div>
              </div>
              <ObjectiveCards items={d.objectives} />
            </>
          );
        }}
      </AsyncView>
      {xp.opportunityRadar && <OpportunityRadarPanel />}
      {xp.decisionBrief ? <DecisionAssistant /> : <><div className="xp-section-head"><div><h2>O que já sabemos</h2><p>Uma síntese viva. Abra os detalhes só quando precisar.</p></div><Link className="link-btn" to="/analises">Ver base completa</Link></div><KnownAnswers /></>}
    </section>
  );
}
